import { SignType, InfluencerEvent } from "../../domain";
import {
  pickBestVariant,
  purchasableOrAll,
  VARIANT_FIELDS,
} from "./components/ProductPage/productAdapter";

export const CUSTOM_IMAGE_TYPE_ID = 400;

export const productAvailability = (product, nowMs = Date.now()) => {
  if (product.vaulted_until && Date.parse(product.vaulted_until) > nowMs) {
    return "https://schema.org/PreOrder";
  }
  if (product.stock !== null && product.stock <= 0) {
    return "https://schema.org/OutOfStock";
  }
  return "https://schema.org/InStock";
};

export function normalizeProductType(productType) {
  const t = (productType || "").toLowerCase().trim();

  if (/8[""]?\s*x\s*10/.test(t)) {
    return { category: "8x10", label: "8×10 Print" };
  }

  if (/5\s*x\s*7/.test(t)) {
    return { category: "5x7", label: "5×7 Print" };
  }

  if (/5\.5[""]?\s*x\s*8\.5/.test(t)) {
    return { category: "5.5x8.5", label: "5.5×8.5 Print" };
  }

  if (
    /8\.5\s*x\s*11/.test(t) ||
    /a4/i.test(t) ||
    /8\.3[""]?\s*x\s*11\.7/.test(t)
  ) {
    return { category: "8.5x11", label: "8.5×11 Print" };
  }

  if (/small\s*print/i.test(t)) {
    return { category: "small-print", label: "Small Print" };
  }

  if (/medium\s*print/i.test(t)) {
    return { category: "medium-print", label: "Medium Print" };
  }
  if (/11[""]?\s*x\s*17/.test(t)) {
    return { category: "11x17", label: "11×17 Poster" };
  }

  if (/11[""]?\s*x\s*14/.test(t)) {
    return { category: "11x14", label: "11×14 Poster" };
  }
  if (/large\s*poster/i.test(t)) {
    return { category: "large-poster", label: "Large Poster" };
  }

  if (/t\s*shirt|tee|sweater/i.test(t)) {
    return { category: "apparel", label: "Apparel" };
  }

  if (/funko|bundle|postcard|backpack/i.test(t)) {
    return { category: "merch", label: "Merch" };
  }

  if (/video|shout\s*out/i.test(t)) {
    return { category: "digital", label: "Digital" };
  }

  return { category: "other", label: productType || "Other" };
}

export function buildSigningTypes(products) {
  const map = products.reduce((acc, p) => {
    const types = p._variants
      ? p._variants.map((v) => v.signed_type)
      : [p.signed_type];
    for (const st of types) {
      acc[st] = acc[st] || {
        category: st,
        label: SignType.toLabel(st),
        count: 0,
      };
      acc[st].count += 1;
    }
    return acc;
  }, {});

  const options = Object.values(map).sort((a, b) => b.count - a.count);

  return [
    { category: "all", label: "All", count: products.length },
    ...options,
  ];
}

export function buildFormatOptions(products) {
  const map = products.reduce((acc, p) => {
    acc[p._formatCategory] = acc[p._formatCategory] || {
      category: p._formatCategory,
      label: p._formatLabel,
      count: 0,
    };
    acc[p._formatCategory].count += 1;
    return acc;
  }, {});

  const options = Object.values(map).sort((a, b) => b.count - a.count);

  return [
    { category: "all", label: "All", count: products.length },
    ...options,
  ];
}

export const UNASSIGNED_CHARACTER = "General";

export function classifyProduct(product, charactersMap) {
  const nameLower = product.name.toLowerCase();

  const isHolo =
    nameLower.includes("holo") ||
    nameLower.includes("gold foil") ||
    nameLower.includes("metallic") ||
    nameLower.includes("gold");

  const { category: formatCategory, label: formatLabel } = normalizeProductType(
    product.product_type,
  );

  const matchedChar =
    product.characterIds?.length > 0 && charactersMap
      ? product.characterIds.find((charId) => !!charactersMap[charId])
      : null;
  const character = matchedChar
    ? charactersMap[matchedChar].name
    : UNASSIGNED_CHARACTER;
  const franchise = matchedChar
    ? charactersMap[matchedChar].franchise_name
    : null;

  const vaNames = (product.fulfillers || []).map((f) => f.name).filter(Boolean);
  const vaInfos = (product.fulfillers || [])
    .filter((f) => f.name)
    .map((f) => ({ name: f.name, route: f.route }));
  const vaAvatars = (product.fulfillers || [])
    .map((f) => f.avatar_url)
    .filter(Boolean);

  const totalPrice = product.price + (product.product_bump || 0);

  return {
    ...product,
    _isFeatured: Boolean(product.featured),
    _isHolo: isHolo,
    _formatCategory: formatCategory,
    _formatLabel: formatLabel,
    _character: character,
    _franchise: franchise,
    _vaNames: vaNames,
    _vaInfos: vaInfos,
    _vaAvatars: vaAvatars,
    _totalPrice: totalPrice,
    _searchText: [product.name, character, ...vaNames].join(" ").toLowerCase(),
  };
}

const popularityLookup = (charactersMap = {}) =>
  Object.values(charactersMap).reduce((acc, char) => {
    acc[char.name] = char.popularity || 0;
    return acc;
  }, {});

const charSort = (popLookup) => (a, b) => {
  if (a.name === UNASSIGNED_CHARACTER) return 1;
  if (b.name === UNASSIGNED_CHARACTER) return -1;
  return (
    (popLookup[b.name] || 0) - (popLookup[a.name] || 0) || b.count - a.count
  );
};

export function buildCharacterList(products, charactersMap) {
  const popLookup = popularityLookup(charactersMap);
  const byChar = products.reduce((acc, p) => {
    acc[p._character] ??= [];
    acc[p._character].push(p);
    return acc;
  }, {});
  return Object.entries(byChar)
    .map(([name, items]) => ({
      name,
      vas: [...new Set(items.flatMap((p) => p._vaNames))],
      count: items.length,
      popularity: popLookup[name] || 0,
    }))
    .sort(charSort(popLookup));
}

// ── Signing-type sort order for variant display ──
const SIGNING_TYPE_ORDER = [
  SignType.livestream,
  SignType.anytime,
  SignType.offCamera,
  SignType.digital,
  SignType.shoutout,
  SignType.audioShoutout,
  SignType.presignedWithVideo,
  SignType.presigned,
  SignType.inPerson,
  SignType.noAutograph,
];

const signingTypeRank = (type) => {
  const idx = SIGNING_TYPE_ORDER.indexOf(type);
  return idx === -1 ? SIGNING_TYPE_ORDER.length : idx;
};

// ── Similarity score: how likely two products are variants of each other ──
// Returns a score 0–4. We require >= 2 to group them.
const variantScore = (a, b) => {
  let score = 0;
  // Parent-child relationship is the strongest signal
  if (
    (a.parent_id && a.parent_id === b.product_id) ||
    (b.parent_id && b.parent_id === a.product_id) ||
    (a.parent_id && a.parent_id === b.parent_id)
  ) {
    score += 2;
  }
  // images differ, so don't group unless parent-child relationship exists
  if (a.image_url && a.image_url !== b.image_url && score === 0) {
    return 0;
  }
  if (a.image_url && a.image_url === b.image_url) score += 1;
  if (a.name === b.name) score += 1;
  if (a.product_type_id === b.product_type_id) score += 1;
  return score;
};

// ── Group products into variant clusters using pairwise similarity ──
const buildVariantGroups = (products) => {
  // Union-Find to cluster products that score high enough
  const parent = products.map((_, i) => i);
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      // eslint-disable-next-line no-param-reassign
      i = parent[i];
    }
    return i;
  };
  const union = (i, j) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[ri] = rj;
  };

  for (let i = 0; i < products.length; i++) {
    for (let j = i + 1; j < products.length; j++) {
      if (variantScore(products[i], products[j]) >= 2) {
        union(i, j);
      }
    }
  }

  // Collect groups preserving insertion order
  const groupMap = new Map();
  for (let i = 0; i < products.length; i++) {
    const root = find(i);
    if (!groupMap.has(root)) groupMap.set(root, []);
    groupMap.get(root).push(products[i]);
  }
  return [...groupMap.values()];
};

// ── Process a group of candidate variant products ──
const processVariantGroup = (group) => {
  if (group.length === 1) {
    return [{ ...group[0], _variants: null }];
  }

  const signedTypes = new Set(group.map((p) => p.signed_type));
  if (signedTypes.size > 1) {
    const rep = pickBestVariant(group);
    const variants = group
      .map((p) =>
        VARIANT_FIELDS.reduce((acc, key) => {
          acc[key] = p[key];
          return acc;
        }, {}),
      )
      .sort(
        (a, b) =>
          signingTypeRank(a.signed_type) - signingTypeRank(b.signed_type),
      );
    const prices = purchasableOrAll(variants).map((v) => v._totalPrice);
    return [
      {
        ...rep,
        _variants: variants,
        _priceRange: { min: Math.min(...prices), max: Math.max(...prices) },
      },
    ];
  }

  return group.map((p) => ({ ...p, _variants: null }));
};

// ── Coalesce products into variant groups ──
export function coalesceProducts(products) {
  return buildVariantGroups(products).flatMap(processVariantGroup);
}

export function buildGroups(products, charactersMap) {
  const popLookup = popularityLookup(charactersMap);

  const map = products.reduce((acc, p) => {
    acc[p._character] ??= [];
    acc[p._character].push(p);
    return acc;
  }, {});

  return Object.entries(map)
    .map(([character, items]) => ({
      character,
      franchise: items[0]?._franchise || null,
      popularity: popLookup[character] || 0,
      vas: Object.values(
        items
          .flatMap((p) => p._vaInfos)
          .reduce((acc, va) => {
            acc[va.name] ??= va;
            return acc;
          }, {}),
      ),
      products: items,
    }))
    .sort((a, b) => {
      if (a.character === UNASSIGNED_CHARACTER) return 1;
      if (b.character === UNASSIGNED_CHARACTER) return -1;
      return (
        b.popularity - a.popularity || b.products.length - a.products.length
      );
    });
}

// ── Process raw API data into classified, coalesced products ──
export function processProducts(data, charactersMap) {
  if (!data) return [];
  const productsArray = Array.isArray(data.products)
    ? data.products
    : Object.values(data.products);
  const byPosition = (a, b) =>
    a.position - b.position || a.product_id - b.product_id;
  const enriched = productsArray
    .filter((p) => p.active && p.product_type_id !== CUSTOM_IMAGE_TYPE_ID)
    .map((p) => classifyProduct(p, charactersMap))
    .toSorted(byPosition);
  return coalesceProducts(enriched);
}

export function extractCustomImageProducts(data, charactersMap) {
  if (!data) return [];
  const productsArray = Array.isArray(data.products)
    ? data.products
    : Object.values(data.products);
  return productsArray
    .filter((p) => p.active && p.product_type_id === CUSTOM_IMAGE_TYPE_ID)
    .map((p) => classifyProduct(p, charactersMap));
}

export const filterLiveEvents = (events) =>
  events.filter(
    (e) => e.event_type !== InfluencerEvent.EVENT_TYPES.NON_LIVE_SIGNING,
  );

export const filterNonLiveEvents = (events) =>
  events.filter(
    (e) => e.event_type === InfluencerEvent.EVENT_TYPES.NON_LIVE_SIGNING,
  );

export const findNextEvent = (events) => {
  const now = new Date();
  const sorted = events
    .filter((e) => new Date(e.next_event_time) >= now)
    .sort((a, b) => new Date(a.next_event_time) - new Date(b.next_event_time));
  return sorted[0] || null;
};

export const sortFns = {
  featured: (a, b) => a.position - b.position || a.product_id - b.product_id,
  "price-asc": (a, b) => a._totalPrice - b._totalPrice,
  "price-desc": (a, b) => b._totalPrice - a._totalPrice,
  "name-asc": (a, b) => a.name.localeCompare(b.name),
  "name-desc": (a, b) => b.name.localeCompare(a.name),
  newest: (a, b) => b.product_id - a.product_id,
};

export function filterSortAndGroup(
  products,
  {
    activeFormat,
    activeSigningType,
    activeCharacter,
    activeVa,
    sortBy,
    searchQuery,
  },
  charactersMap,
) {
  const filtered = products.filter((p) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p._searchText.includes(q)) return false;
    }

    if (activeCharacter && p._character !== activeCharacter) {
      return false;
    }

    if (activeFormat !== "all" && p._formatCategory !== activeFormat) {
      return false;
    }

    if (activeSigningType && activeSigningType !== "all") {
      const hasMatch = p._variants
        ? p._variants.some((v) => v.signed_type === activeSigningType)
        : p.signed_type === activeSigningType;
      if (!hasMatch) return false;
    }

    if (activeVa && !p._vaNames.includes(activeVa)) {
      return false;
    }

    return true;
  });

  const sorted = sortFns[sortBy]
    ? [...filtered].sort(sortFns[sortBy])
    : filtered;

  const groups = buildGroups(sorted, charactersMap);

  const hasActiveFilters = !!(
    searchQuery ||
    activeCharacter ||
    activeVa ||
    activeFormat !== "all" ||
    (activeSigningType && activeSigningType !== "all")
  );

  const featuredProducts = hasActiveFilters
    ? []
    : sorted.filter((p) => p._isFeatured);

  const featuredGroup =
    featuredProducts.length > 0
      ? {
          character: "Featured",
          popularity: Infinity,
          vas: [...new Set(featuredProducts.flatMap((p) => p._vaNames))],
          products: featuredProducts,
        }
      : null;

  return { groups, featuredGroup };
}

export function buildFilterLabels(
  { activeCharacter, activeFormat, activeSigningType, activeVa, searchQuery },
  formatOptions,
  signingTypes,
) {
  return [
    searchQuery ? `"${searchQuery}"` : undefined,
    activeCharacter,
    activeFormat !== "all"
      ? formatOptions.find((f) => f.category === activeFormat)?.label ??
        activeFormat
      : undefined,
    activeSigningType && activeSigningType !== "all"
      ? signingTypes?.find((s) => s.category === activeSigningType)?.label ??
        activeSigningType
      : undefined,
    activeVa,
  ].filter(Boolean);
}

export const buildGroupsKey = ({
  activeFormat,
  activeSigningType,
  activeCharacter,
  activeVa,
  sortBy,
  searchQuery,
}) =>
  `${activeFormat}|${activeSigningType || ""}|${activeCharacter || ""}|${
    activeVa || ""
  }|${sortBy}|${searchQuery || ""}`;

export const countVisible = (groups) =>
  groups.reduce((sum, g) => sum + g.products.length, 0);

export const COMPACT_THRESHOLD = 3;

export const partitionIntoSegments = (groups, threshold = COMPACT_THRESHOLD) =>
  groups.reduce((segments, group) => {
    const isCompact = group.products.length <= threshold;

    if (!isCompact) {
      segments.push({ type: "full", group });
      return segments;
    }

    const lastSegment = segments.at(-1);
    if (lastSegment?.type === "compact") {
      lastSegment.groups.push(group);
    } else {
      segments.push({ type: "compact", groups: [group] });
    }

    return segments;
  }, []);

export function buildVaList(products, talentMap) {
  const map = products
    .flatMap((p) => p.fulfillers || [])
    .filter((f) => !!f.name)
    .reduce((acc, f) => {
      acc[f.name] ??= [];
      acc[f.name].push(f);
      return acc;
    }, {});

  return Object.entries(map)
    .map(([name, fulfillers]) => {
      const first = fulfillers[0];
      const talentData = talentMap?.[first.influencer_id];
      return {
        name,
        chars: talentData?.characters?.map((c) => c.name) || [],
        count: fulfillers.length,
        avatarUrl: talentData?.avatar_url || first.avatar_url,
        influencer_id: first.influencer_id,
      };
    })
    .filter((v) => v.count >= 1)
    .sort((a, b) => b.count - a.count);
}
